import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useState } from 'react';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecoilValue } from 'recoil';
import { NavMenuItem } from '../../../interfaces/Settings/FrontendSettings';
import { sharedColors } from '../../../utils/Style';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { useThemeColor } from '../hooks/useThemeColor';
import { OffersButton } from './OffersButton';
import { bodyWidth } from '../constants/Style';
import { useIsMobile } from '../hooks/useIsMobile';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

type HeaderMenuItemProps = {
  menuItem: NavMenuItem;
};

const SecondLevelAccordion = ({ menuItem }: HeaderMenuItemProps) => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <EditableStyleWrapper path='appHeaderMobileSettings.secondLevelRowStyle'>
        <Box
          onClick={() => {
            if (menuItem.items.length) {
              setOpen((x) => !x);
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2.5,
            py: 1,
            justifyContent: 'space-between',
            ...settings.settingsV2?.appHeaderMobileSettings
              ?.secondLevelRowStyle,
          }}
        >
          <EditableStyleWrapper path='appHeaderMobileSettings.secondLevelTextStyle'>
            <Typography
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
              }}
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: sharedColors.gray6,
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.secondLevelTextStyle,
              }}
            >
              {menuItem.name}
            </Typography>
          </EditableStyleWrapper>

          {!!menuItem.items.length && (
            <EditableStyleWrapper path='appHeaderMobileSettings.secondLevelChevronStyle'>
              <IconButton
                size='small'
                sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
              >
                <ExpandMoreIcon
                  sx={{
                    ...settings.settingsV2?.appHeaderMobileSettings
                      ?.secondLevelChevronStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}
        </Box>
      </EditableStyleWrapper>

      {open &&
        menuItem.items.map((child, i) => (
          <EditableStyleWrapper path='appHeaderMobileSettings.thirdLevelRowStyle'>
            <Box
              key={i}
              sx={{
                px: 2.5,
                py: 1,
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.thirdLevelRowStyle,
              }}
            >
              <EditableStyleWrapper path='appHeaderMobileSettings.thirdLevelTextStyle'>
                <Typography
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                  }}
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: sharedColors.gray6,
                    ...settings.settingsV2?.appHeaderMobileSettings
                      ?.thirdLevelTextStyle,
                  }}
                >
                  {child.name}
                </Typography>
              </EditableStyleWrapper>
            </Box>
          </EditableStyleWrapper>
        ))}
    </Box>
  );
};

const TopLevelAccordion = ({ menuItem }: HeaderMenuItemProps) => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <EditableStyleWrapper path='appHeaderMobileSettings.firstLevelRowStyle'>
        <Box
          onClick={() => {
            if (menuItem.items.length) {
              setOpen((x) => !x);
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            justifyContent: 'space-between',
            ...settings.settingsV2?.appHeaderMobileSettings?.firstLevelRowStyle,
          }}
        >
          <EditableStyleWrapper path='appHeaderMobileSettings.firstLevelTextStyle'>
            <Typography
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
              }}
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: sharedColors.gray7,
                ...settings.settingsV2?.appHeaderMobileSettings
                  ?.firstLevelTextStyle,
              }}
            >
              {menuItem.name}
            </Typography>
          </EditableStyleWrapper>

          {!!menuItem.items.length && (
            <EditableStyleWrapper path='appHeaderMobileSettings.firstLevelChevronStyle'>
              <IconButton
                sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
              >
                <ExpandMoreIcon
                  sx={{
                    ...settings.settingsV2?.appHeaderMobileSettings
                      ?.firstLevelChevronStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          )}
        </Box>
      </EditableStyleWrapper>

      <EditableStyleWrapper path='appHeaderMobileSettings.firstLevelDividerStyle'>
        <Divider
          sx={{
            ...settings.settingsV2?.appHeaderMobileSettings
              ?.firstLevelDividerStyle,
          }}
        />
      </EditableStyleWrapper>

      {open &&
        menuItem.items.map((child, i) => (
          <SecondLevelAccordion key={i} menuItem={child} />
        ))}
    </Box>
  );
};

export const AppHeaderMobile = () => {
  const settings = useRecoilValue(frontendSettingsAtom);
  const [showMenu, setShowMenu] = useState(false);

  const { primary, secondary } = useThemeColor();

  const totalQuantity = 0;

  const logo = settings.settingsV1.storefront_header_logo;
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <EditableStyleWrapper path='appHeaderMobileSettings.rootStyle'>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            ...settings.settingsV2?.appHeaderMobileSettings?.rootStyle,
          }}
        >
          <EditableStyleWrapper path='appHeaderMobileSettings.menuIconStyle'>
            <IconButton onClick={() => setShowMenu((x) => !x)}>
              <MenuIcon
                sx={{
                  ...settings.settingsV2?.appHeaderMobileSettings
                    ?.menuIconStyle,
                }}
              />
            </IconButton>
          </EditableStyleWrapper>

          {logo ? (
            <EditableStyleWrapper path='appHeaderMobileSettings.logoStyle'>
              <Box
                component='img'
                src={logo}
                sx={{
                  height: 55,
                  width: 'auto',
                  ...settings.settingsV2?.appHeaderMobileSettings?.logoStyle,
                }}
              />
            </EditableStyleWrapper>
          ) : (
            <Typography>
              {settings.settingsV1.storefront_navbar_text}
            </Typography>
          )}

          <Box sx={{ ml: 'auto' }} />

          <Box>
            <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
              <IconButton>
                <FavoriteBorderIcon
                  sx={{
                    ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>

            <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
              <IconButton>
                <EditableStyleWrapper path='appHeaderMobileSettings.iconBadgeStyle'>
                  <Badge
                    badgeContent={totalQuantity.toString()}
                    sx={{
                      '& .MuiBadge-badge': {
                        color: secondary,
                        backgroundColor: primary,
                        ...settings.settingsV2?.appHeaderSettings
                          ?.iconBadgeStyle,
                      },
                    }}
                    overlap='circular'
                    style={{ transform: 'translate(25px, -15px)' }}
                  />
                </EditableStyleWrapper>
                <ShoppingBasketIcon
                  sx={{
                    ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>

            <OffersButton />

            <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
              <IconButton>
                <PersonOutlineIcon
                  sx={{
                    ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                  }}
                />
              </IconButton>
            </EditableStyleWrapper>
          </Box>
        </Box>
      </EditableStyleWrapper>

      <Divider />

      <Box
        p={1.5}
        sx={{
          backgroundColor: showMenu ? sharedColors.white : sharedColors.gray3,
        }}
      >
        <TextField
          fullWidth
          size='small'
          sx={{
            backgroundColor: sharedColors.white,
            height: 'fit-content',
          }}
          placeholder='Aramak için yazınız...'
        />
      </Box>

      {showMenu && (
        <EditableStyleWrapper path='appHeaderMobileSettings.navMenuStyle'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              ...settings.settingsV2?.appHeaderMobileSettings?.navMenuStyle,
            }}
          >
            {settings.menu.map((item, i) => (
              <TopLevelAccordion key={i} menuItem={item} />
            ))}
          </Box>
        </EditableStyleWrapper>
      )}
    </Box>
  );
};

const MenuItemButton = ({ menuItem }: HeaderMenuItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { primary, secondary, ternary } = useThemeColor();

  const settings = useRecoilValue(frontendSettingsAtom);

  const vertical = menuItem.items.reduce(
    (acc, item) => acc && !item.items.length,
    true,
  );

  return (
    <Box
      onMouseEnter={(ev) => setAnchorEl(ev.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <EditableStyleWrapper path='appHeaderSettings.navMenuSettings.topLevelItemStyle'>
        <Button
          sx={{
            color: secondary,
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            lineHeight: '20px',
            p: '15px 18px',
            height: 50,
            transition: 'color 0.5s ease',
            textTransform: 'none',
            '&:hover': {
              color: ternary,
              backgroundColor: 'transparent',
            },
            ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
              ?.topLevelItemStyle,
          }}
        >
          {menuItem.name}
        </Button>
      </EditableStyleWrapper>

      <Popper open={!!anchorEl && !!menuItem.items.length} anchorEl={anchorEl}>
        <EditableStyleWrapper path='appHeaderSettings.navMenuSettings.popperStyle'>
          <Paper
            sx={{
              display: 'flex',
              flexDirection: vertical ? 'column' : 'row',
              gap: vertical ? 0 : 5,
              p: 1.5,
              ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
                ?.popperStyle,
            }}
          >
            {menuItem.items.map((child, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  alignItems: 'start',
                }}
              >
                <EditableStyleWrapper path='appHeaderSettings.navMenuSettings.secondLevelItemStyle'>
                  <Button
                    sx={{
                      color: sharedColors.gray7,
                      mb: child.items.length ? 0.25 : 0,
                      p: 0,
                      minWidth: 0,
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: primary,
                        backgroundColor: 'transparent',
                      },
                      ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
                        ?.secondLevelItemStyle,
                    }}
                  >
                    {child.name}
                  </Button>
                </EditableStyleWrapper>

                {child.items.map((item) => (
                  <EditableStyleWrapper
                    key={item.id}
                    path='appHeaderSettings.navMenuSettings.thirdLevelItemStyle'
                  >
                    <Button
                      sx={{
                        color: sharedColors.gray6,
                        textTransform: 'none',
                        p: 0,
                        minWidth: 0,
                        transition: 'color 0.3s ease',
                        '&:hover': {
                          color: primary,
                          backgroundColor: 'transparent',
                        },
                        ...settings.settingsV2?.appHeaderSettings
                          ?.navMenuSettings?.thirdLevelItemStyle,
                      }}
                    >
                      {item.name}
                    </Button>
                  </EditableStyleWrapper>
                ))}
              </Box>
            ))}
          </Paper>
        </EditableStyleWrapper>
      </Popper>
    </Box>
  );
};

export const AppHeaderDesktop = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const { primary, secondary } = useThemeColor();

  const totalQuantity = 0;

  const logo = settings.settingsV1.storefront_header_logo;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <EditableStyleWrapper path='appHeaderSettings.rootStyle'>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            py: 1.5,
            ...settings.settingsV2?.appHeaderSettings?.rootStyle,
          }}
        >
          <EditableStyleWrapper path='appHeaderSettings.innerStyle'>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: bodyWidth,
                gap: 1.5,
                alignItems: 'center',
                ...settings.settingsV2?.appHeaderSettings?.innerStyle,
              }}
            >
              {logo ? (
                <EditableStyleWrapper path='appHeaderSettings.logoStyle'>
                  <Box
                    component='img'
                    src={logo}
                    sx={{
                      cursor: 'pointer',
                      height: 80,
                      width: 'auto',
                      ...settings.settingsV2?.appHeaderSettings?.logoStyle,
                    }}
                  />
                </EditableStyleWrapper>
              ) : (
                <Typography>
                  {settings.settingsV1.storefront_navbar_text}
                </Typography>
              )}

              <Box sx={{ ml: 1 }}>
                {!!settings.settingsV1.storefront_facebook_link && (
                  <EditableStyleWrapper path='appHeaderSettings.socialMediaStyle'>
                    <IconButton>
                      <FacebookIcon
                        sx={{
                          color: primary,
                          ...settings.settingsV2?.appHeaderSettings
                            ?.socialMediaStyle,
                        }}
                      />
                    </IconButton>
                  </EditableStyleWrapper>
                )}

                {!!settings.settingsV1.storefront_twitter_link && (
                  <EditableStyleWrapper path='appHeaderSettings.socialMediaStyle'>
                    <IconButton>
                      <XIcon
                        sx={{
                          color: primary,
                          ...settings.settingsV2?.appHeaderSettings
                            ?.socialMediaStyle,
                        }}
                      />
                    </IconButton>
                  </EditableStyleWrapper>
                )}

                {!!settings.settingsV1.storefront_instagram_link && (
                  <EditableStyleWrapper path='appHeaderSettings.socialMediaStyle'>
                    <IconButton>
                      <InstagramIcon
                        sx={{
                          color: primary,
                          ...settings.settingsV2?.appHeaderSettings
                            ?.socialMediaStyle,
                        }}
                      />
                    </IconButton>
                  </EditableStyleWrapper>
                )}

                {!!settings.settingsV1.storefront_youtube_link && (
                  <EditableStyleWrapper path='appHeaderSettings.socialMediaStyle'>
                    <IconButton>
                      <YouTubeIcon
                        sx={{
                          color: primary,
                          ...settings.settingsV2?.appHeaderSettings
                            ?.socialMediaStyle,
                        }}
                      />
                    </IconButton>
                  </EditableStyleWrapper>
                )}

                {!!settings.settingsV1.storefront_linkedin_link && (
                  <EditableStyleWrapper path='appHeaderSettings.socialMediaStyle'>
                    <IconButton>
                      <LinkedInIcon
                        sx={{
                          color: primary,
                          ...settings.settingsV2?.appHeaderSettings
                            ?.socialMediaStyle,
                        }}
                      />
                    </IconButton>
                  </EditableStyleWrapper>
                )}
              </Box>

              <Box sx={{ ml: 'auto' }} />

              <TextField
                size='small'
                sx={{
                  width: 290,
                  backgroundColor: sharedColors.white,
                  height: 'fit-content',
                }}
                placeholder='Aramak için yazınız...'
              />
              <Box>
                <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
                  <IconButton>
                    <FavoriteBorderIcon
                      sx={{
                        ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>

                <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
                  <IconButton>
                    <EditableStyleWrapper path='appHeaderSettings.iconBadgeStyle'>
                      <Badge
                        badgeContent={totalQuantity.toString()}
                        sx={{
                          '& .MuiBadge-badge': {
                            color: secondary,
                            backgroundColor: primary,
                            ...settings.settingsV2?.appHeaderSettings
                              ?.iconBadgeStyle,
                          },
                        }}
                        overlap='circular'
                        style={{ transform: 'translate(25px, -15px)' }}
                      />
                    </EditableStyleWrapper>
                    <ShoppingBasketIcon
                      sx={{
                        ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>

                <OffersButton />

                <EditableStyleWrapper path='appHeaderSettings.iconStyle'>
                  <IconButton>
                    <PersonOutlineIcon
                      sx={{
                        ...settings.settingsV2?.appHeaderSettings?.iconStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              </Box>
            </Box>
          </EditableStyleWrapper>
        </Box>
      </EditableStyleWrapper>

      <EditableStyleWrapper path='appHeaderSettings.navMenuSettings.rootStyle'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: primary,
            width: '100%',
            justifyContent: 'center',
            ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
              ?.rootStyle,
          }}
        >
          <EditableStyleWrapper path='appHeaderSettings.navMenuSettings.innerStyle'>
            <Box
              sx={{
                width: bodyWidth,
                display: 'flex',
                flexDirection: 'row',
                ...settings.settingsV2?.appHeaderSettings?.navMenuSettings
                  ?.innerStyle,
              }}
            >
              {settings.menu.map((item, i) => (
                <MenuItemButton key={i} menuItem={item} />
              ))}
            </Box>
          </EditableStyleWrapper>
        </Box>
      </EditableStyleWrapper>
    </Box>
  );
};

export const AppHeader = () => {
  const isMobile = useIsMobile();

  return isMobile ? <AppHeaderMobile /> : <AppHeaderDesktop />;
};
